import type { Capability, EvaluatedFlags, PersonPrefsDto } from '@fyr/shared';
import type { Rag } from '@fyr/shared';
export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    /** A machine-readable reason, when the api gives one (e.g. `microsoft-connect`). */
    public code?: string,
  ) {
    super(message);
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`/api${path}`, {
    method,
    credentials: 'same-origin',
    headers: body === undefined ? {} : { 'content-type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  // #153 the Platform console has its own session: a lapsed one goes back to the console's sign-in, not Fyr's
  if (res.status === 401 && !path.startsWith('/auth/') && !path.startsWith('/platform/auth/')) {
    window.location.assign(path.startsWith('/platform') ? '/platform' : '/login');
  }
  if (!res.ok) {
    const j = (await res.json().catch(() => null)) as { message?: string; code?: string } | null;
    throw new ApiError(res.status, j?.message ?? res.statusText, j?.code);
  }
  return (await res.json()) as T;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body ?? {}),
  patch: <T>(path: string, body: unknown) => request<T>('PATCH', path, body),
  put: <T>(path: string, body: unknown) => request<T>('PUT', path, body),
  del: <T>(path: string) => request<T>('DELETE', path),
};

/** §16 the global person (one human, many client accounts). */
export interface PersonInfo {
  id: string;
  displayName: string;
  email: string;
  givenName: string | null;
  familyName: string | null;
  status: 'pending' | 'active' | 'disabled';
  defaultAffiliation: string | null;
  groups: string[];
  isPlatformAdmin: boolean;
}

/** A client/org context the person can switch to (one per tenant account). */
export interface ContextInfo {
  tenantId: string;
  tenantName: string;
  tenantType: string;
  organisation: string | null;
  affiliation: string;
  jobRole: string | null;
  isTenantAdmin: boolean;
}

/** How this session signed in (#147 adds emailed codes and passkeys). */
export type SignedInVia = 'oidc' | 'sso' | 'dev' | 'lti' | 'device' | 'email' | 'passkey';

export interface PendingMe {
  pending: true;
  person: PersonInfo;
  contexts: ContextInfo[];
  signedInVia: SignedInVia;
  /** The person's own display preferences (per account; absent from an api older than the command bar). */
  prefs?: PersonPrefsDto;
}

/** Signed in with an active context: the active account's fields are flat for convenience. */
export interface Me {
  pending: false;
  person: PersonInfo;
  contexts: ContextInfo[];
  signedInVia: SignedInVia;
  id: string;
  displayName: string;
  email: string;
  handle: string;
  jobRole: string | null;
  affiliation: string;
  organisation: string | null;
  isTenantAdmin: boolean;
  /** §23 M12.2 capabilities beyond project roles (tenant admins hold them all). */
  capabilities: Capability[];
  /** Downloads / exports are allowed (unless the tenant restricts them to export_data holders). */
  exportsAllowed: boolean;
  /** §23 M12.3 what Ibexstone has switched on for this organisation — every flag, evaluated for this person. */
  features: EvaluatedFlags;
  isPlatformAdmin: boolean;
  locale: string;
  tenant: { id: string; name: string };
  /** The person's own display preferences (per account; absent from an api older than the command bar). */
  prefs?: PersonPrefsDto;
}

export type MeResponse = Me | PendingMe;

/** Sign out of Fyr and — for SSO sessions — of Authentik too. */
export async function signOut() {
  // #155.2 in the app: this device stops getting the account's pushes (a shared phone)
  if ((window as { Capacitor?: unknown }).Capacitor)
    await import('./native-push').then((m) => m.forgetHere()).catch(() => undefined);
  const r = await api.post<{ ok: true; endSessionUrl: string | null }>('/auth/logout');
  window.location.assign(r.endSessionUrl ?? '/login');
}

export interface ProjectSummary {
  id: string;
  name: string;
  rag: Rag | null;
  stage: string | null;
  programme: { id: string; name: string; ref: string | null } | null;
  myRole: string;
  canCreateProjects: boolean;
  /** #142 M19.2 not on the project — something in it is shared with me. */
  guest?: boolean; /** Data lifecycle: set on an archived project (only in an admin's Archived / All view). */
  archive: import('@fyr/shared').ProjectArchiveDto | null;
}
